import { ListPage, ListPagination } from '@/components/app/list-page';
import type { ListColumn } from '@/components/app/list-page';
import { useSidePanel } from '@/components/app/side-panel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { format, startOfDay, subMonths } from 'date-fns';
import { CalendarIcon, Megaphone, MoreVertical } from 'lucide-react';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';
import { toast } from 'sonner';
import {
  useDeleteCampaign,
  useDuplicateCampaign,
  useListCampaignInsights,
  useListCampaigns,
  usePauseCampaign,
  useResumeCampaign,
} from '../../api';
import type { Campaign, CampaignInsightsParams } from '../../api/types';
import { campaignHasBudget } from '../../utils';
import {
  type CampaignStatusFilterValue,
  campaignMatchesStatusFilter,
} from '../campaign-mobile/campaign-mobile-utils';
import { CampaignPanel } from '../campaign-panel';
import { CreateCampaignDialog } from '../create-campaign-modal';
import { DeleteCampaignDialog } from '../delete-campaign-dialog';
import { DuplicateCampaignDialog } from '../duplicate-campaign-dialog';
import { NoBudgetDialog } from '../no-budget-dialog';

import { CampaignDestinations } from './campaign-destinations';
import {
  CORE_METRICS,
  CoreMetric,
  ResultCount,
  formatCampaignMoney,
} from './campaign-metrics';
import { CampaignMetricsPanel } from './campaign-metrics-panel';
import { getCampaignDestinationLabels } from './campaign-presentation';
import { MetricLabel } from './metric-label';

// --- Helpers ---

// --- Error badge (shown beside the campaign name) ---

function getCampaignErrorMessage(effectiveStatus: string): string | null {
  switch (effectiveStatus) {
    case 'WITH_ISSUES':
      return 'This campaign has issues and may not be delivering. Check Meta Ads Manager for details.';
    case 'DISAPPROVED':
      return "This campaign was rejected by Meta. Review Meta's advertising policies and resubmit.";
    case 'PENDING_BILLING_INFO':
      return 'This campaign is paused because billing information is missing or invalid.';
    default:
      return null;
  }
}

// --- Campaign Sub-Components ---

function CampaignStatusSwitch({
  campaign,
  isActive,
}: {
  campaign: Campaign;
  isActive: boolean;
}) {
  const { execute: pauseCampaign, isExecuting: isPausing } = usePauseCampaign();
  const { execute: resumeCampaign, isExecuting: isResuming } =
    useResumeCampaign();
  const [showNoBudgetDialog, setShowNoBudgetDialog] = React.useState(false);

  const handleToggle = (checked: boolean) => {
    if (!checked) {
      pauseCampaign(campaign.id);
      return;
    }

    if (!campaignHasBudget(campaign)) {
      setShowNoBudgetDialog(true);
      return;
    }

    resumeCampaign(campaign.id);
  };

  return (
    <>
      <Switch
        aria-label={isActive ? 'Pause campaign' : 'Resume campaign'}
        checked={isActive}
        disabled={isPausing || isResuming}
        onCheckedChange={handleToggle}
        // Toggling delivery must not ALSO open the campaign behind the row.
        onClick={(event) => event.stopPropagation()}
      />
      <NoBudgetDialog
        campaignName={campaign.name}
        onOpenChange={setShowNoBudgetDialog}
        open={showNoBudgetDialog}
      />
    </>
  );
}

export function CampaignActions({ campaign }: { campaign: Campaign }) {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const { open: openPanel } = useSidePanel();
  const [duplicateDialogOpen, setDuplicateDialogOpen] = React.useState(false);
  const {
    execute: deleteCampaign,
    isExecuting: isDeleting,
    isSuccess: isDeleteSuccess,
  } = useDeleteCampaign();
  const { execute: duplicateCampaign, isExecuting: isDuplicating } =
    useDuplicateCampaign();

  const handleDuplicateConfirm = () => {
    duplicateCampaign(campaign.id, {
      onSuccess: () => setDuplicateDialogOpen(false),
    });
  };

  // The Meta campaign id — what Settings > Claire ad campaigns asks for.
  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(campaign.id);
      toast.success('Campaign ID copied');
    } catch {
      // Clipboard refused (unfocused window, permissions): show the id so it
      // can still be copied by hand.
      toast.error(`Couldn't copy. Campaign ID: ${campaign.id}`);
    }
  };

  React.useEffect(() => {
    if (isDeleteSuccess && deleteDialogOpen) {
      setDeleteDialogOpen(false);
    }
  }, [isDeleteSuccess, deleteDialogOpen]);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label={`Actions for ${campaign.name}`}
            className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
            size="icon"
            variant="ghost"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem
            onSelect={() =>
              openPanel(
                <CampaignPanel
                  key={`${campaign.id}-edit`}
                  campaignId={campaign.id}
                  initialEditOpen
                />,
                { presentation: 'overlay' }
              )
            }
          >
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={isDuplicating}
            onSelect={() => setDuplicateDialogOpen(true)}
          >
            {isDuplicating ? 'Duplicating...' : 'Duplicate'}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void handleCopyId()}>
            Copy campaign ID
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            disabled={isDeleting}
            onSelect={() => setDeleteDialogOpen(true)}
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteCampaignDialog
        campaignName={campaign.name}
        isDeleting={isDeleting}
        onConfirm={() => deleteCampaign(campaign.id)}
        onOpenChange={setDeleteDialogOpen}
        open={deleteDialogOpen}
      />

      <DuplicateCampaignDialog
        campaignName={campaign.name}
        isDuplicating={isDuplicating}
        onConfirm={handleDuplicateConfirm}
        onOpenChange={setDuplicateDialogOpen}
        open={duplicateDialogOpen}
      />
    </>
  );
}

// --- Main Component ---

/**
 * The advertising campaigns list, on the shared `ListPage`.
 *
 * Replaces a hand-rolled TanStack table AND the parallel `CampaignMobileList`
 * that restated the same fields as phone rows. Both layouts now render from the
 * one `columns` config, so a column added here cannot silently miss the phone.
 *
 * Search, the status filter, the insights date range and pagination stay HERE —
 * `rows` is simply what the shell renders (see the list-page README).
 */
export function CampaignList({
  readOnly = false,
  onCampaignSelect,
  initialCampaignId,
  initialAdId,
  initialCreateOpen = false,
}: {
  /** Hide write controls (status toggle, New Campaign, edit/delete). */
  readOnly?: boolean;
  /**
   * Optional selection override for the read-only admin view. The normal
   * Campaigns page opens campaign details in its shared side panel.
   */
  onCampaignSelect?: (id: string) => void;
  initialCampaignId?: string;
  initialAdId?: string;
  initialCreateOpen?: boolean;
} = {}) {
  const { open: openPanel } = useSidePanel();
  const [isCreateOpen, setIsCreateOpen] = React.useState(initialCreateOpen);
  React.useEffect(() => {
    if (initialCampaignId)
      openPanel(
        <CampaignPanel
          key={initialCampaignId}
          campaignId={initialCampaignId}
          initialAdId={initialAdId}
        />,
        { presentation: 'overlay' }
      );
  }, [initialCampaignId, initialAdId, openPanel]);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] =
    React.useState<CampaignStatusFilterValue>('all');
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);

  // Date range — defaults to the past month.
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(
    () => ({
      from: startOfDay(subMonths(new Date(), 1)),
      to: new Date(),
    })
  );

  const insightsParams = React.useMemo<CampaignInsightsParams>(() => {
    if (dateRange?.from && dateRange?.to) {
      return {
        since: format(dateRange.from, 'yyyy-MM-dd'),
        until: format(dateRange.to, 'yyyy-MM-dd'),
      };
    }
    return {};
  }, [dateRange]);

  const dateRangeLabel = React.useMemo(() => {
    if (!dateRange?.from) return 'Select date range';
    if (!dateRange.to) return format(dateRange.from, 'd MMMM yyyy');
    return `${format(dateRange.from, 'd MMMM yyyy')} - ${format(
      dateRange.to,
      'd MMMM yyyy'
    )}`;
  }, [dateRange]);

  const { campaigns, isLoading, isError, error, refetch } = useListCampaigns();

  // One batched request fetches insights for every campaign at once — the
  // per-row fan-out it replaces tripped Meta's rate limit on page load.
  const {
    insightsByCampaignId,
    isLoading: isInsightsLoading,
    isError: insightsFailed,
  } = useListCampaignInsights(insightsParams);

  const filtered = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return campaigns.filter((campaign) => {
      if (!campaignMatchesStatusFilter(campaign, statusFilter)) return false;
      if (!term) return true;
      return (campaign.name ?? '').toLowerCase().includes(term);
    });
  }, [campaigns, search, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  // A filter that shortens the list must not strand the viewer on a page that
  // no longer exists.
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const rows = React.useMemo(
    () =>
      filtered.slice(safePageIndex * pageSize, (safePageIndex + 1) * pageSize),
    [filtered, safePageIndex, pageSize]
  );

  // Meta reports the ad account's currency with the figures; the campaign's
  // stored currency is the fallback for older API responses.
  const currencyOf = (campaign: Campaign) =>
    insightsByCampaignId.get(campaign.id)?.currency ?? campaign.currency;

  const columns: ListColumn<Campaign>[] = [
    {
      id: 'toggle',
      header: 'Off/On',
      width: 'w-20',
      // Write control — hidden in read-only (admin) mode, and dropped on the
      // phone, which has never carried it.
      hidden: readOnly,
      cell: (campaign) => (
        <CampaignStatusSwitch
          campaign={campaign}
          isActive={campaign.status === 'ACTIVE'}
        />
      ),
    },
    {
      id: 'name',
      header: 'Name',
      mobile: 'primary',
      cell: (campaign) => {
        const errorMessage = getCampaignErrorMessage(campaign.effectiveStatus);
        return (
          <div>
            <div className="mb-1 flex items-center gap-2">
              <span className="font-medium">{campaign.name}</span>
              {errorMessage && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge className="cursor-default" variant="destructive">
                        Error
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>{errorMessage}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {/* Phones keep the old row: name, spend and results only. */}
            <div className="max-md:hidden">
              <CampaignDestinations
                labels={getCampaignDestinationLabels(campaign)}
              />
            </div>
          </div>
        );
      },
    },
    // The old phone row's spend and results. Desktop shows the metric columns.
    {
      id: 'mobileSpent',
      mobile: 'secondary',
      mobileOnly: true,
      cell: (campaign) => (
        <div className="text-sm">
          {isInsightsLoading
            ? '...'
            : insightsFailed
              ? '—'
              : formatCampaignMoney(
                  insightsByCampaignId.get(campaign.id)?.totals.spend ?? 0,
                  currencyOf(campaign)
                )}
        </div>
      ),
    },
    {
      id: 'mobileResults',
      mobile: 'trailing',
      mobileOnly: true,
      cell: (campaign) => (
        <ResultCount
          campaign={campaign}
          totals={insightsByCampaignId.get(campaign.id)?.totals}
          loading={isInsightsLoading}
          failed={insightsFailed}
        />
      ),
    },
    ...CORE_METRICS.map(
      ({ id, label }): ListColumn<Campaign> => ({
        id,
        header: <MetricLabel label={label} />,
        align: 'right',
        cell: (campaign) => (
          <CoreMetric
            id={id}
            totals={insightsByCampaignId.get(campaign.id)?.totals}
            currency={currencyOf(campaign)}
            loading={isInsightsLoading}
            failed={insightsFailed}
          />
        ),
      })
    ),
  ];

  const openCampaign = (campaign: Campaign) => {
    if (onCampaignSelect) {
      onCampaignSelect(campaign.id);
      return;
    }
    openPanel(<CampaignPanel key={campaign.id} campaignId={campaign.id} />, {
      presentation: 'overlay',
    });
  };

  return (
    <>
      <ListPage<Campaign>
        config={{
          title: 'Campaigns',
          stickyHeader: true,
          columns,
          rows,
          rowKey: (campaign) => campaign.id,
          onRowClick: openCampaign,
          rowActions: (campaign) => (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground max-md:hidden"
                onClick={(event) => {
                  event.stopPropagation();
                  openPanel(
                    <CampaignMetricsPanel
                      campaign={campaign}
                      params={insightsParams}
                      period={dateRangeLabel}
                    />,
                    { presentation: 'overlay' }
                  );
                }}
              >
                More metrics
              </Button>
              {!readOnly && <CampaignActions campaign={campaign} />}
            </div>
          ),
          searchPlaceholder: 'Search campaigns',
          search,
          onSearchChange: (value) => {
            setSearch(value);
            setPageIndex(0);
          },
          primaryAction: readOnly
            ? undefined
            : {
                label: 'New Campaign',
                mobileLabel: 'New',
                onClick: () => setIsCreateOpen(true),
              },
          // Full-width row above the search box: the status strip wants the
          // whole column, which the `toolbar` slot (inside the non-wrapping
          // search row) cannot give it.
          filters: (
            <Tabs
              onValueChange={(value) => {
                setStatusFilter(value as CampaignStatusFilterValue);
                setPageIndex(0);
              }}
              value={statusFilter}
            >
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="ACTIVE">Active</TabsTrigger>
                <TabsTrigger value="PAUSED">Paused</TabsTrigger>
              </TabsList>
            </Tabs>
          ),
          toolbar: (
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline">
                  <CalendarIcon className="size-4" />
                  <span className="hidden sm:inline">{dateRangeLabel}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-0">
                <Calendar
                  defaultMonth={dateRange?.from}
                  disabled={(date) =>
                    date > new Date() || date < new Date('1900-01-01')
                  }
                  mode="range"
                  numberOfMonths={2}
                  onSelect={setDateRange}
                  selected={dateRange}
                />
              </PopoverContent>
            </Popover>
          ),
          isLoading,
          isError,
          errorMessage: error?.message || 'Failed to load campaigns',
          onRetry: () => void refetch(),
          footer: filtered.length > 0 && (
            <div className="mt-4 shrink-0 border-t border-border/60 pt-4">
              <ListPagination
                label="campaigns"
                onPageChange={setPageIndex}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPageIndex(0);
                }}
                pageIndex={safePageIndex}
                pageSize={pageSize}
                total={filtered.length}
                wrap
              />
            </div>
          ),
          empty: {
            icon: Megaphone,
            title:
              search || statusFilter !== 'all'
                ? 'No matching campaigns'
                : 'No campaigns yet',
            description:
              search || statusFilter !== 'all'
                ? 'No campaign matches that search or filter.'
                : readOnly
                  ? 'This organization has no campaigns yet.'
                  : 'Create your first campaign to start advertising and reaching your audience.',
            // No explicit action: the shell falls back to `primaryAction`,
            // which is itself absent in read-only mode — so an admin viewer is
            // never offered a write they cannot perform.
          },
        }}
      />

      {!readOnly && (
        <CreateCampaignDialog
          onOpenChange={setIsCreateOpen}
          open={isCreateOpen}
        />
      )}
    </>
  );
}
